<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-4xl mx-auto space-y-8">
      <header class="flex justify-between items-center bg-zinc-900 p-6 rounded-2xl shadow-sm border border-zinc-800">
        <h1 class="text-3xl font-bold text-green-500">Codifyo Music</h1>
        
        <div v-if="isConnected" class="flex items-center gap-4">
          <div class="flex items-center gap-2 bg-green-500/10 px-4 py-2 rounded-full border border-green-500/20">
            <span class="relative flex h-3 w-3">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
            <span class="text-green-400 font-medium text-sm">Connecté à Spotify</span>
          </div>
          <button @click="disconnect" class="text-sm text-zinc-400 hover:text-red-400 transition-colors">Déconnexion</button>
        </div>
      </header>

      <main class="space-y-6">
        <div v-if="loading" class="text-center py-12 text-zinc-500">
          Chargement du statut Spotify...
        </div>

        <div v-else-if="!isConnected" class="bg-zinc-900 p-12 rounded-2xl shadow-sm border border-zinc-800 text-center space-y-6">
          <div class="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-green-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.24 1.02h.06zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2v.02zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.6.18-1.2.72-1.38 4.2-1.261 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.54-1.02.72-1.56.3v.019z"/>
            </svg>
          </div>
          <h2 class="text-2xl font-bold">Connectez votre compte Spotify</h2>
          <p class="text-zinc-400 max-w-md mx-auto">
            Pour générer des playlists et synchroniser vos titres likés, Codifyo a besoin d'accéder à votre compte Spotify.
          </p>
          <button @click="connectSpotify" :disabled="authLoading" class="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-zinc-950 px-8 py-3 rounded-full font-bold transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100 shadow-lg shadow-green-500/20">
            {{ authLoading ? 'Connexion en cours...' : 'Associer mon compte Spotify' }}
          </button>
        </div>

        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <button 
            @click="syncTracks" 
            :disabled="syncing"
            class="flex flex-col items-center justify-center p-8 bg-zinc-900 border border-zinc-800 rounded-2xl hover:border-green-500/50 hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <div class="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 mb-4 group-hover:scale-110 transition-transform">
              <svg v-if="syncing" class="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
            </div>
            <span class="font-bold text-lg text-zinc-200">Synchroniser la bibliothèque</span>
            <span class="text-sm text-zinc-500 mt-2 text-center">Importe vos titres likés depuis Spotify</span>
          </button>

          <router-link 
            to="/analytics" 
            class="flex flex-col items-center justify-center p-8 bg-zinc-900 border border-zinc-800 rounded-2xl hover:border-emerald-500/50 hover:bg-zinc-800 transition-all group"
          >
            <div class="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 mb-4 group-hover:scale-110 transition-transform">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
            </div>
            <span class="font-bold text-lg text-zinc-200">Statistiques</span>
            <span class="text-sm text-zinc-500 mt-2 text-center">Top artistes et genres</span>
          </router-link>

          <router-link 
            to="/events" 
            class="flex flex-col items-center justify-center p-8 bg-zinc-900 border border-zinc-800 rounded-2xl hover:border-purple-500/50 hover:bg-zinc-800 transition-all group"
          >
            <div class="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-500 mb-4 group-hover:scale-110 transition-transform">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
            </div>
            <span class="font-bold text-lg text-zinc-200">Créer un événement</span>
            <span class="text-sm text-zinc-500 mt-2 text-center">Générer une playlist collaborative</span>
          </router-link>

          <router-link 
            to="/analytics/playlists" 
            class="flex flex-col items-center justify-center p-8 bg-zinc-900 border border-zinc-800 rounded-2xl hover:border-cyan-500/50 hover:bg-zinc-800 transition-all group"
          >
            <div class="w-16 h-16 rounded-full bg-cyan-500/10 flex items-center justify-center text-cyan-500 mb-4 group-hover:scale-110 transition-transform">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path></svg>
            </div>
            <span class="font-bold text-lg text-zinc-200">Mes Playlists</span>
            <span class="text-sm text-zinc-500 mt-2 text-center">Gérer les playlists automatiques</span>
          </router-link>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useFetch } from '@vueuse/core'

import { useToast } from '../../composables/useToast'

const router = useRouter()
const { success, error: showError } = useToast()
const isConnected = ref(false)
const loading = ref(true)
const authLoading = ref(false)
const syncing = ref(false)

const checkStatus = async () => {
    try {
        const token = localStorage.getItem('apiToken')
        const response = await fetch('/api/music/spotify/status', {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        const data = await response.json()
        isConnected.value = data.connected
        syncing.value = data.isSyncing || false
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

const connectSpotify = async () => {
  authLoading.value = true
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/spotify/auth-url', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    const data = await response.json()
    if (data.url) {
      window.location.href = data.url
    }
  } catch (e) {
    console.error(e)
    authLoading.value = false
  }
}

const disconnect = async () => {
  try {
    const token = localStorage.getItem('apiToken')
    await fetch('/api/music/spotify/disconnect', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    isConnected.value = false
  } catch (e) {
    console.error(e)
  }
}

const syncTracks = async () => {
  syncing.value = true
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/sync', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    const data = await response.json();
    if (data.error) {
        showError(data.error)
        syncing.value = false
        return
    }
    
    // Poll le statut toutes les 5 secondes pour savoir quand ça se termine
    const interval = setInterval(async () => {
        await checkStatus();
        if (!syncing.value) {
            clearInterval(interval);
            success('Synchronisation terminée avec succès !');
        }
    }, 5000);

  } catch (e) {
    console.error(e)
    showError('Erreur lors de la synchronisation.')
    syncing.value = false
  }
}

onMounted(() => {
  checkStatus()
})
</script>
